import React, { useEffect } from "react";
import { X } from "lucide-react";

/**
 * Right-hand detail panel.
 *
 * The panel had no way to look at one record — every page was a table and the
 * table was the whole story, so anything not worth a column simply wasn't
 * visible anywhere. A drawer keeps the list in place behind it, which matters
 * when you're working through a queue rather than visiting one row.
 */
export function Drawer({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  width = 520,
}: {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: number;
}) {
  // Escape closes it, and the body doesn't scroll behind it. Both are things
  // people try without thinking about it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(24, 27, 46, 0.34)",
        backdropFilter: "blur(2px)",
        zIndex: 300,
        display: "flex",
        justifyContent: "flex-end",
      }}
    >
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: width,
          background: "var(--bg)",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          boxShadow: "-24px 0 60px rgba(24, 27, 46, 0.18)",
          animation: "adminDrawerIn 0.22s cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        <header
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            padding: "20px 22px",
            borderBottom: "1px solid var(--border)",
            background: "rgba(255,255,255,0.9)",
            backdropFilter: "blur(12px)",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 850, letterSpacing: "-0.03em" }}>{title}</div>
            {subtitle && (
              <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 3 }}>
                {subtitle}
              </div>
            )}
          </div>
          <button className="btn btn-sm" onClick={onClose} aria-label="Close">
            <X size={14} />
          </button>
        </header>

        <div style={{ flex: 1, overflowY: "auto", padding: 22 }}>{children}</div>

        {footer && (
          <footer
            style={{
              padding: "16px 22px",
              borderTop: "1px solid var(--border)",
              background: "rgba(255,255,255,0.92)",
              backdropFilter: "blur(12px)",
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            {footer}
          </footer>
        )}
      </aside>
    </div>
  );
}

/** A labelled value. The drawer is mostly these. */
export function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div style={{ display: "flex", gap: 12, padding: "9px 0", alignItems: "baseline" }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: "var(--text-muted)",
          width: 132,
          flexShrink: 0,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 13,
          color: "var(--text-primary)",
          fontWeight: 600,
          wordBreak: "break-word",
          flex: 1,
          fontFamily: mono
            ? "ui-monospace, SFMono-Regular, Menlo, monospace"
            : undefined,
        }}
      >
        {value ?? "—"}
      </div>
    </div>
  );
}

/** Groups fields under a heading inside the drawer. */
export function Section({
  title,
  children,
  action,
}: {
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <section
      style={{
        background: "#fff",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-lg)",
        padding: "16px 18px",
        marginBottom: 14,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 6,
        }}
      >
        <h3
          style={{
            fontSize: 12,
            fontWeight: 850,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            color: "var(--text-secondary)",
            margin: 0,
          }}
        >
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}
